import { createSelector, defaultMemoize, createSelectorCreator } from 'reselect';
import { get, maxBy, flow, head, find, omit, orderBy, isEqual, uniqBy, cloneDeep, isEmpty, sortBy } from 'lodash';
import moment from 'moment';

import { parsePhoneNumber } from 'modules/user/utils/formatPhoneNumber';
import { appointmentHavePermission, makeAppointmentUpdatePermissions } from 'modules/common/selectors/permissions';
import { PROVIDER, CUSTOMER } from 'modules/common/constants/conversationParticipantsTypes';
import {
  ONGOING,
  OPENED,
  CLOSED,
  COMPLETED,
  CANCELED,
  COULD_NOT_CONNECT,
  appointmentEditableStateMessagesMap,
} from 'modules/common/constants/appointmentStatuses';
import { SCHEDULED } from 'modules/queues/constants/queueTypes';
import {
  CONNECTING, IN_PROGRESS, CREATED,
  DECLINED, UNANSWERED, PENDING, DROPPED,
} from 'modules/common/constants/conversationStatuses';
import { userAttributeLabels } from 'modules/user/constants/userAttributeLabels';
import { getCurrentLanguage } from 'modules/language/selectors/language';
import { getUsersReadPermissions, getUsersUpdatePermissions } from 'modules/session/selectors/permissions';
import * as appointmentPermissionResources from 'modules/common/constants/appointmentPermissionResources';
import { userGenderOptions, userGenderMessages } from 'modules/user/constants/userGender';
import { userTitles, userTitlesMessages } from 'modules/user/constants/userTitles';
import { appointmentFormFieldLabels, usersFieldLabels } from '../constants/formFieldLabels';
import { FLEXIBLE } from '../constants/schedulingTypes';
import { getTimeSlots, formatTimeSlots } from './contracts';

export const noPermissionsValuePlaceholder = '*****';

const getCurrentUserInfo = state => get(state, 'auth.userInfo', {});
const getAppointmentServiceProviders = state => get(state, 'appointmentsManagement.appointmentConfig.providers', {});
const getAppointment = state => get(state, 'appointmentInfo.currentAppointment');
const getOrganizationConfig = state => get(state, 'common.organizationConfig');
const getAppointmentServiceGroupedSpecialties = state => get(state, 'appointmentsManagement.appointmentConfig.providerGroupSpecialties', []);

export const getUserGenderOptions = createSelector(
  [intl => intl],
  (intl) => userGenderOptions.map(item => ({
    key: item,
    value: intl.formatMessage(userGenderMessages[item]),
  })),
);

export const getUserTitleOptions = createSelector(
  [intl => intl],
  intl => userTitles.map(item => ({
    key: item,
    value: intl.formatMessage(userTitlesMessages[item]),
  })),
);

const appointmentFormFieldLabelsModified = {
  ...appointmentFormFieldLabels,
  'flexible.from': appointmentFormFieldLabels.flexibleFrom,
  'flexible.to': appointmentFormFieldLabels.flexibleTo,
};

export const getAttributeLabels = createSelector(
  [getOrganizationConfig, totalAppointments => totalAppointments],
  ({ generalConfig: { externalIdName = [] } }, totalAppointments) => ({
    ...appointmentFormFieldLabelsModified,
    ...Object.entries(usersFieldLabels).reduce((acc, [key, value]) => ({
      ...acc,
      [`customer.${ key }`]: key === 'externalId'
        ? get(externalIdName.find(({ locale }) => locale.toLowerCase() === getCurrentLanguage()), 'value', 'ID')
        : value,
      ...Array.from(Array(totalAppointments)).reduce((acc2, item, index) => ({
        ...acc2,
        [`patient.${ index }.${ key }`]: key === 'externalId'
          ? get(externalIdName.find(({ locale }) => locale.toLowerCase() === getCurrentLanguage()), 'value', 'ID')
          : value,
      }), {}),
    }), {}),
  }),
);

export const appointmentUpdateFormFieldLabelsModified = createSelector(
  [serviceConfig => serviceConfig],
  (serviceConfig) => {
    const fieldLabels = { ...appointmentFormFieldLabelsModified, ...userAttributeLabels };
    const isFlexible = serviceConfig.queueType === SCHEDULED && serviceConfig.schedulingType === FLEXIBLE;

    if (isFlexible) {
      fieldLabels.date = appointmentFormFieldLabels.startDate;
    }

    return fieldLabels;
  },
);

const selectProvidrObjectBuilder = (key, value, disabled) => ({ key, value, disabled });

export const getAppointmentProvidersOptions = createSelector(
  [
    getAppointment,
    getAppointmentServiceProviders,
    getAppointmentServiceGroupedSpecialties,
    (_, intl) => intl,
    (_, intl, isProviderRequired) => isProviderRequired,
    (_, intl, isProviderRequired, isWalkIn) => isWalkIn,
  ],
  (appointment, providers, specialties, intl, isProviderRequired, isWalkIn = false) => {
    if (isEmpty(providers)) return [];

    const providersGroup = { key: 'PROVIDERS_GROUP', value: intl.formatMessage({ id: 'General.ProvidersGroup', defaultMessage: '** Providers group' }) };
    const currAppointmentHasProvider = appointment?.participants.find(item => item.participantType === PROVIDER);
    const unavailableMessage = intl.formatMessage({ id: 'Clinician.Appointment.Handoff.Create.Provider.Unavailable', defaultMessage: '** (Unavailable)' });
    let result = providers.providers?.map(item => selectProvidrObjectBuilder(
      item.userId,
      `${ item.fullName } ${ !item.isAvailable ? unavailableMessage : '' }`,
      !item.isAvailable,
    ));
    if (isWalkIn && specialties.length > 0 && !isProviderRequired) {
      result = sortBy(specialties.map(({ specialty, isAvailable }) => selectProvidrObjectBuilder(
        specialty.id,
        `${ specialty.name } ${ intl.formatMessage({ id: 'SpecialtyGroup.SelectionListView', defaultMessage: '** specialty group' }) }`,
        !isAvailable,
        )), item => item.value.toLowerCase()).concat(result);
    }
    if ((!isProviderRequired || (!currAppointmentHasProvider && appointment)) && providers.isProviderGroupAvailable) result.unshift(providersGroup);

    return result;
  },
);

export const cantChangeProviderDueToConversationStatus = createSelector(
  [getAppointment],
  ({ status, conversations }) => {
    const { status: lastConversationStatus } = maxBy(conversations, 'start') || {};

    if (!lastConversationStatus) return false;

    return ([ONGOING, OPENED].includes(status) && [CREATED, CONNECTING, IN_PROGRESS].includes(lastConversationStatus))
      || (conversations.some((conversation) => ![DECLINED, UNANSWERED, PENDING, DROPPED].includes(conversation.status))
        && [CREATED, CONNECTING].includes(lastConversationStatus));
  },
);

export const getAppointmentDetailsInitialValues = createSelector(
  [getAppointment],
  appointment => {
    if (!appointment) return {};

    const providerId = find(appointment.participants, { participantType: PROVIDER })?.user.userId || appointment.providerGroupSpecialty?.id;
    const customerDetails = find(appointment.participants, { participantType: CUSTOMER }).user;
    const patientDetails = get(appointment, 'subjects[0].user', customerDetails);
    const customerIsPatient = customerDetails.userId === patientDetails.userId;

    const schedule = flow([
      schedules => schedules.filter(({ status }) => status === 'CREATED'),
      head,
    ])(appointment.schedules);
    const appointmentStatus = [COMPLETED, CANCELED, COULD_NOT_CONNECT].includes(appointment.status) ? CLOSED : OPENED;
    const isFlexible = appointment.service.config.schedulingType === FLEXIBLE;

    return {
      appointmentId: appointment.appointmentId,
      externalId: appointment.externalId,
      conversationType: appointment.allowedConversationTypes[0],
      createdAt: moment(appointment.createdTimestamp).format('DD/MM/YYYY'),
      serviceId: appointment.service.serviceId,
      providerUserId: providerId || 'PROVIDERS_GROUP',
      date: schedule.startTime,
      endDate: isFlexible ? schedule.endTime : null,
      timeSlot: `${ schedule.startTime }-${ schedule.endTime }`,
      flexible: isFlexible ? { from: schedule.startTime, to: schedule.endTime } : null,
      status: appointmentStatus,
      state: appointment.status,
      customerIsPatient,
      customer: {
        ...omit(customerDetails, 'profile'),
        ...customerDetails.profile,
        dateOfBirth: customerDetails.profile.dateOfBirth && moment(Number(customerDetails.profile.dateOfBirth)).isValid()
          ? moment(Number(customerDetails.profile.dateOfBirth)).format('DD/MM/YYYY')
          : '',
        phoneNumber: parsePhoneNumber(customerDetails.profile.phoneNumber),
        initUser: { ...omit(customerDetails, 'profile'), ...customerDetails.profile },
      },
      patient: {
        ...omit(patientDetails, 'profile'),
        ...patientDetails.profile,
        dateOfBirth: patientDetails.profile.dateOfBirth && moment(Number(patientDetails.profile.dateOfBirth)).isValid()
          ? moment(Number(patientDetails.profile.dateOfBirth)).format('DD/MM/YYYY')
          : '',
        phoneNumber: parsePhoneNumber(patientDetails.profile.phoneNumber),
        initUser: { ...omit(patientDetails, 'profile'), ...patientDetails.profile },
      },
    };
  },
);

export const appointmentEditableStateMessages = [COMPLETED, CANCELED, COULD_NOT_CONNECT]
  .reduce((acc, curr) => {
    acc[curr] = appointmentEditableStateMessagesMap[curr];

    return acc;
  }, {});

export const getAppointmentStateOptions = createSelector(
  [getAppointment, (state, intl) => intl],
  (appointment, intl) => {
    const result = [];

    Object.entries(appointmentEditableStateMessages).forEach(([key, value]) => {
      if (!appointmentHavePermission(appointment, { [`${ appointmentPermissionResources.APPOINTMENTS_STATUS }_${ key }`]: ['update'] })) return;

      result.push({
        key,
        value: intl.formatMessage(value),
        disabled: (key === COMPLETED && appointment.state !== ONGOING) || (key === CANCELED && appointment.state === ONGOING),
      });
    });

    return result;
  },
);

export const getUpdatePermissions = appointment => ({
  canEditAppointment: appointmentHavePermission(appointment, { [appointmentPermissionResources.APPOINTMENTS]: ['update'] }),
  canEditStatus: appointmentHavePermission(appointment, { [appointmentPermissionResources.APPOINTMENTS_STATUS]: ['update'] }),
  canEditUser: appointmentHavePermission(appointment, { ['USERS']: ['update'] }),
});

export const getAppointmentCreateInitialValues = createSelector(
  [getCurrentUserInfo, getUsersReadPermissions, getUsersUpdatePermissions],
  (userInfo, readPermissions, updatePermissions) => {
    const customerInfo = {
      ...omit(userInfo, 'profile'),
      ...userInfo.profile,
      dateOfBirth: userInfo.profile.dateOfBirth && moment(Number(userInfo.profile.dateOfBirth)).isValid()
        ? moment(+userInfo.profile.dateOfBirth).format('DD/MM/YYYY')
        : '',
      phoneNumber: parsePhoneNumber(userInfo.profile.phoneNumber),
    };

    return {
      customerIsPatient: true,
      customerIsPatientIndex: 0,
      customer: {
        ...customerInfo,
        initUser: {
          ...omit(userInfo, 'profile'),
          ...userInfo.profile,
          isRestrictedUser: !readPermissions.canSeeUserData,
          permissions: { read: readPermissions, update: updatePermissions },
        },
      },
      patient: [{
        initUser: 'customer',
      }],
      attachments: [],
    };
  },
);

export const getTimeSlotsOptions = createSelector(
  [getTimeSlots, getAppointment, (state, formValues) => formValues],
  (timeSlots, appointment, formValues = {}) => {
    const { providerUserId, date } = formValues;

    let result = timeSlots.map((item) => ({
      key: `${ item.from }-${ item.to }`,
      value: `${ moment(item.from).format('HH:mm') } - ${ moment(item.to).format('HH:mm') }`,
    }));

    const schedule = flow([
      schedules => schedules.filter(({ status }) => status === 'CREATED'),
      head,
    ])(appointment.schedules);

    const currSelectedItem = {
      key: `${ schedule.startTime }-${ schedule.endTime }`,
      value: `${ moment(schedule.startTime).format('HH:mm') } - ${ moment(schedule.endTime).format('HH:mm') }`,
    };

    const provider = appointment.participants.find(item => item.participantType === PROVIDER);
    const isSameProvider = (provider && provider.user.userId) === (providerUserId === 'PROVIDERS_GROUP' ? undefined : providerUserId);
    const isSameDay = date === schedule.startTime || moment(date).isSame(schedule.startTime, 'day');

    if (result && (isSameProvider || providerUserId === 'PROVIDERS_GROUP') && isSameDay && !result.some(item => item.key === currSelectedItem.key)) {
      result.push(currSelectedItem);
      result = orderBy(result, 'key');
    }

    return result;
  },
);

const slotInterval = 60000;

const createDeepEqualSelector = createSelectorCreator(
  defaultMemoize,
  (currValue, prevValue) => currValue === prevValue || isEqual(currValue, prevValue),
);

export const getFlexibleToSlotsOptions = createDeepEqualSelector(
  [getTimeSlots, getAppointment, (state, config, flexibleFrom, flexibleDate, providerUserId) => ({ flexibleFrom, flexibleDate, providerUserId })],
  (timeSlotsRaw, appointment, { flexibleFrom, flexibleDate, providerUserId }) => {
    const timeSlots = formatTimeSlots(cloneDeep(timeSlotsRaw));
    let endTimeSlots = [];

    const schedule = flow([
      schedules => schedules.filter(({ status }) => status === 'CREATED'),
      head,
    ])(appointment.schedules);

    const currSelectedItem = {
      key: schedule.endTime,
      value: moment(schedule.endTime).format('HH:mm'),
    };

    if (!timeSlots.length) return [currSelectedItem];

    const startTime = flexibleFrom || schedule.startTime;

    const nextTimeSlot = timeSlots.find(item =>
      item.from === schedule.endTime || (moment(startTime).isBetween(item.from, item.to)) || startTime === item.from) || { to: schedule.endTime };

    const { fixedAppointmentDuration, maxAppointmentDuration } = appointment.service.config;

    let endTime;
    if (fixedAppointmentDuration) {
      endTime = moment(startTime).add(fixedAppointmentDuration, 'minute');

      endTimeSlots.push({
        value: endTime.format('HH:mm'),
        key: endTime.valueOf(),
      });
    } else if (maxAppointmentDuration) {
      endTime = moment(startTime).add(maxAppointmentDuration, 'minute');
      if (endTime > nextTimeSlot.to) endTime = nextTimeSlot.to;
    } else {
      endTime = moment(nextTimeSlot.to);
    }

    const endDate = moment(flexibleDate).startOf('day').valueOf();
    const isSameEndDate = moment(flexibleDate).isSame(startTime, 'day');
    const maxTime = isSameEndDate
      ? moment(flexibleDate).endOf('day').valueOf()
      : moment(startTime).add(1, 'day').valueOf();
    const endTimeValue = endTime.valueOf();
    let nextTimeInterval = startTime;

    while (nextTimeInterval < endTimeValue && nextTimeInterval < maxTime) {
      nextTimeInterval += slotInterval;
      if (nextTimeInterval > endTimeValue) nextTimeInterval = endTimeValue;

      if (nextTimeInterval >= endDate && nextTimeInterval <= maxTime) {
        endTimeSlots.push({
          key: moment(nextTimeInterval).valueOf(),
          value: moment(nextTimeInterval).format('HH:mm'),
        });
      }
    }

    const isSameDate = flexibleDate && moment(schedule.startTime).isSame(new Date(flexibleFrom), 'day');
    const provider = appointment.participants.find(item => item.participantType === PROVIDER);
    const isSameProvider = (provider && provider.user.userId) === (providerUserId === 'PROVIDERS_GROUP' ? undefined : providerUserId);

    if (isSameDate && isSameProvider && !endTimeSlots.some(item => item.key === currSelectedItem.key) && currSelectedItem.key > startTime && currSelectedItem.key < endDate) {
      endTimeSlots.push(currSelectedItem);
      endTimeSlots = orderBy(endTimeSlots, 'key');
    }

    return uniqBy(endTimeSlots, item => item.key);
  },
);

export const getFlexibleFromSlotsOptions = createDeepEqualSelector(
  [getTimeSlots, getAppointment, (state, config, scheduleDay, providerUserId) => ({ scheduleDay, providerUserId })],
  (operationTimeSlots, appointment, { scheduleDay, providerUserId }) => {
    const timeSlots = formatTimeSlots(cloneDeep(operationTimeSlots));
    let startTimeSlots = [];

    const schedule = flow([
      schedules => schedules.filter(({ status }) => status === 'CREATED'),
      head,
    ])(appointment.schedules);

    const currSelectedItem = {
      key: schedule.startTime,
      value: moment(schedule.startTime).format('HH:mm'),
    };

    const { fixedAppointmentDuration } = appointment.service.config;

    timeSlots.forEach((item) => {
      const endTimeValue = !fixedAppointmentDuration
        ? moment(item.to).startOf('minute').valueOf()
        : moment(item.to).subtract(fixedAppointmentDuration - 1, 'minute').startOf('minute').valueOf();

      let nextTimeInterval = item.from;
      if (moment(nextTimeInterval).startOf('minute').valueOf() !== nextTimeInterval) {
        nextTimeInterval = moment(nextTimeInterval).add(1, 'minute').startOf('minute').valueOf();
      }

      const startDateValue = moment(scheduleDay).endOf('day').valueOf();

      while (nextTimeInterval < endTimeValue && nextTimeInterval <= startDateValue) {
        startTimeSlots.push({
          key: nextTimeInterval,
          value: moment(nextTimeInterval).format('HH:mm'),
        });

        nextTimeInterval += slotInterval;
      }
    });

    const isSameDate = scheduleDay && moment(schedule.startTime).isSame(new Date(scheduleDay), 'day');
    const provider = appointment.participants.find(item => item.participantType === PROVIDER);
    const isSameProvider = (provider && provider.user.userId) === (providerUserId === 'PROVIDERS_GROUP' ? undefined : providerUserId);

    if (isSameDate && isSameProvider && startTimeSlots && !startTimeSlots.some(item => item.key === currSelectedItem.key)) {
      startTimeSlots.push(currSelectedItem);
      startTimeSlots = orderBy(startTimeSlots, 'key');
    }

    return uniqBy(startTimeSlots, item => item.key);
  },
);

export const getUpdateCurrentAppointmentPermissions = createSelector(
  [getAppointment],
  makeAppointmentUpdatePermissions,
);
